import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {ErrorMessage, Field, Form, Formik} from "formik";
import * as Yup from "yup";
import * as LinkFactory from "../../routing/LinkFactory";
import * as Utils from "../../utils/Utils";
import {getFromLocal, saveToLocal} from "../../utils/Utils";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {setPin} from "../../reduxStore/pinStore/middleware/pinMiddleware";
import axiosAgent from "../../api/AxiosAgent";
import api from "../../api/api";
import CustomModal from "../../utils/CustomModal";
import privacyIcon from "../../assets/images/content/privacy-icon.svg";
import {UserTypes} from "../../constants/UserTypes";
import Loader from "../../utils/Loader";
import {useProSubscription} from "../../contexts/ProSubscriptionContext";
import {FaLock} from "react-icons/fa";
import {
    FaChartPie,
    FaFileInvoiceDollar,
    FaUserTie,
    FaUserCircle,
    FaUsers,
    FaMoneyCheckAlt,
    FaWallet,
    FaUndo,
    FaExchangeAlt,
    FaIdCard,
    FaStream,
    FaMoneyBillWave,
    FaFileMedical,
    FaRoute,
    FaFileContract,
    FaFileSignature,
    FaTachometerAlt,
    FaBell,
    FaEnvelope,
    FaCommentDots,
    FaStar,
    FaCreditCard,
    FaCalendarAlt,
    FaHandshake,
    FaBriefcase,
    FaGlobeAmericas,
    FaTags,
    FaBars,
    FaLightbulb,
    FaCrown,
    FaPeopleArrows,
    FaChevronDown,
    FaSearch
} from "react-icons/fa";

const COLLAPSED_GROUPS_KEY = "sideNavCollapsedGroups";

/**
 * Collapse state is scoped to the current login (keyed on LAST_LOGIN), so every
 * fresh login starts with all groups collapsed while toggles made during the
 * session are remembered across page loads.
 */
const readCollapsedGroups = (): Record<string, boolean> => {
    try {
        const stored = JSON.parse(localStorage.getItem(COLLAPSED_GROUPS_KEY) || "{}");
        if (stored?.session !== (getFromLocal("LAST_LOGIN") || "")) {
            return {};
        }
        return stored.groups || {};
    } catch {
        return {};
    }
};

const writeCollapsedGroups = (groups: Record<string, boolean>) => {
    try {
        localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify({
            session: getFromLocal("LAST_LOGIN") || "",
            groups
        }));
    } catch {
        // storage unavailable — collapse state simply won't persist
    }
};

interface NavGroupProps {
    label: string;
    icon?: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    /** Start this group open on a fresh login instead of collapsed. */
    defaultExpanded?: boolean;
    children: React.ReactNode;
}

/** A collapsible side-nav section: a clickable group label that shows/hides its items. */
const NavGroup: React.FC<NavGroupProps> = ({label, icon, className, style, defaultExpanded = false, children}) => {
    // A toggle made during this login always wins; otherwise fall back to the
    // group's default, which is collapsed unless the group opts out.
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        const stored = readCollapsedGroups()[label];
        return stored === undefined ? !defaultExpanded : stored;
    });

    const toggle = () => {
        const next = !collapsed;
        setCollapsed(next);
        writeCollapsedGroups({...readCollapsedGroups(), [label]: next});
    };

    return (
        <>
            <div
                className={`side-nav-group-label side-nav-group-toggle${collapsed ? " collapsed" : ""}${className ? ` ${className}` : ""}`}
                style={style}
                role="button"
                tabIndex={0}
                aria-expanded={!collapsed}
                onClick={toggle}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle();
                    }
                }}
            >
                {icon}
                {label}
                <FaChevronDown className="side-nav-group-chevron" />
            </div>
            {!collapsed && children}
        </>
    );
};

const SideNav: React.FC = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [userType, setUserType] = useState("");
    const [completedStep, setCompletedStep] = useState("");
    const [rcicProfileCompleted, setRcicProfileCompleted] = useState<boolean | null>(null);
    const [rcicCoreCompleted, setRcicCoreCompleted] = useState<boolean | null>(null);
    const [legalSetupDone, setLegalSetupDone] = useState<boolean | null>(null);
    const [pendingPinRoute, setPendingPinRoute] = useState<string | null>(null);
    // Route to continue to once the RCIC sets their security PIN (first access to
    // Case Management or Wallet). Non-null shows the PIN setup modal.
    const [pinSetupRoute, setPinSetupRoute] = useState<string | null>(null);
    const [pinSetupError, setPinSetupError] = useState('');
    const pin = useAppSelector((state) => state.pinRx.pin);
    const [showPin] = useState(false);
    const [disableAccessButton, setDisableAccessButton] = useState<boolean>(true);
    const [invalidPin, setInvalidPin] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalMessage, setModalMessage] = useState('');
    const [loader, setLoader] = useState(false);
    const [pendingConsultationCount, setPendingConsultationCount] = useState(0);
    const [pendingPracticeContinuityCount, setPendingPracticeContinuityCount] = useState(0);
    const pinSet = (pin: string) => {
        dispatch(setPin(pin));
    }
    const verifyPin = () => {
        setLoader(true);
        axiosAgent.requests.post(api.RCIC_PROFILE + "/verify-pin", {pin: pin?.pin}).then(res => {
            setLoader(false);
            if (res.data === true) {
                navigate(pendingPinRoute!);
                setPendingPinRoute(null);
                resetPinInput();
            } else {
                resetPinInput();
                setInvalidPin('The PIN you entered is incorrect. Please try again.');
            }
        }).catch(() => {
            setLoader(false);
        })
    }

    const resetPinInput = () => {
        for (let index = 0; index < 4; index++) {
            const inputElement = document.getElementById(`pinInput-${index}`) as HTMLInputElement;
            if (inputElement) {
                inputElement.value = ''; // Clear the input field
            }
        }
        const firstElement = document.getElementById(`pinInput-0`)
        firstElement && firstElement.focus(); // Focus on the first input field
    }

    const forgetPinReq = () => {
        axiosAgent.requests.post(api.RCIC_PROFILE + "/forgot-pin", {}).then(() => {
            setPendingPinRoute(null);
            setModalTitle("Reset Request");
            setModalMessage("A new PIN reset link is sent to your registered email address. Please check your email and follow the instructions to reset your Case Management PIN.");
            setShowModal(true);
        })
    }


    const fetchPendingConsultations = async () => {
        try {
            const response = await axiosAgent.requests.post(
                `${api.APPOINTMENT_SLOT_BOOKING}/slots-only-by-logged-in-rcic?page=0&size=100`,
                {appointmentSlotBookingStatus: "INITIALIZED"}
            );
            const count = response?.data?.totalElements ?? 0;
            setPendingConsultationCount(count);
        } catch {
            // silently ignore — badge is non-critical
        }
    };

    const fetchPendingPracticeContinuity = async () => {
        try {
            const response = await axiosAgent.requests.get(api.PRACTICE_CONTINUITY_INCOMING);
            const requests = response?.data || [];
            setPendingPracticeContinuityCount(requests.length);
        } catch {
            // silently ignore — badge is non-critical
        }
    };

    const syncRcicCompletionStatus = async (): Promise<boolean | null> => {
        try {
            const res = await axiosAgent.requests.post(api.RCIC_PROFILE + "/completion-status", {});
            const data = res?.data;
            const allCompleted = !!(
                data?.licenseVerificationCompleted &&
                data?.initialAgreementSetupCompleted &&
                data?.retainerAgreementSetupCompleted &&
                data?.signatureUploaded &&
                data?.serviceOfferedSetupCompleted
            );
            const coreCompleted = !!(
                data?.licenseVerificationCompleted &&
                data?.serviceOfferedSetupCompleted
            );
            const legalDone = !!(
                data?.initialAgreementSetupCompleted &&
                data?.retainerAgreementSetupCompleted &&
                data?.signatureUploaded
            );
            localStorage.setItem("RCIC_PROFILE_COMPLETION_STATUS", allCompleted ? "true" : "false");
            localStorage.setItem("RCIC_CORE_COMPLETED", coreCompleted ? "true" : "false");
            localStorage.setItem("RCIC_LEGAL_SETUP_DONE", legalDone ? "true" : "false");
            window.dispatchEvent(new CustomEvent("rcic-lock-status-updated"));
            setRcicProfileCompleted(allCompleted);
            setRcicCoreCompleted(coreCompleted);
            setLegalSetupDone(legalDone);
            return allCompleted;
        } catch (error) {
            console.error("Error syncing RCIC completion status:", error);
            const fallback = getFromLocal("RCIC_PROFILE_COMPLETION_STATUS") === "true";
            setRcicProfileCompleted(fallback);
            return fallback;
        }
    };

    const ensureRcicCompletionStatus = async (): Promise<boolean> => {
        if (rcicProfileCompleted !== null) return rcicProfileCompleted;
        const refreshed = await syncRcicCompletionStatus();
        return refreshed === true;
    };

    useEffect(() => {

        const interval = setInterval(() => {
            const newCookieData = Utils.getUserType();
            if (newCookieData !== undefined && newCookieData !== userType) {
                setUserType(newCookieData);
                setCompletedStep(Utils.getCompletedStep());
                if (newCookieData === UserTypes.RCIC) {
                    void syncRcicCompletionStatus();
                    fetchPendingConsultations();
                    void fetchPendingPracticeContinuity();
                }
                clearInterval(interval); // Stop the interval when cookieData is undefined
            }
            if (newCookieData === undefined) {
                setUserType("");
            }
        }, 500); // Check every second

        return () => clearInterval(interval); // Cleanup the interval on component unmount
    }, [userType]);

    useEffect(() => {
        if (userType === UserTypes.RCIC) {
            void syncRcicCompletionStatus();
        } else {
            setRcicProfileCompleted(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType, location.pathname]);


    // Re-fetch pending count whenever the RCIC navigates (catches accept/decline actions)
    useEffect(() => {
        if (userType === UserTypes.RCIC) {
            fetchPendingConsultations();
            void fetchPendingPracticeContinuity();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType, location.pathname]);

    const handleRCICRedirection = (path: string) => {
        void (async () => {
            if (await ensureRcicCompletionStatus()) {
                navigate(path);
                return;
            }
            setModalTitle("Complete Onboarding");
            setModalMessage("Please complete platform onboarding to access this feature.");
            setShowModal(true);
        })();
    }

    const handleLegalAgreementsAccess = () => {
        void (async () => {
            // Legal Agreements is a setup page — only require the core onboarding steps
            if (rcicCoreCompleted !== null) {
                rcicCoreCompleted ? navigate(LinkFactory.legal_correspondences) : showOnboardingRequired();
                return;
            }
            try {
                const res = await axiosAgent.requests.post(api.RCIC_PROFILE + "/completion-status", {});
                const d = res?.data;
                const core = !!(d?.licenseVerificationCompleted && d?.serviceOfferedSetupCompleted);
                setRcicCoreCompleted(core);
                core ? navigate(LinkFactory.legal_correspondences) : showOnboardingRequired();
            } catch {
                showOnboardingRequired();
            }
        })();
    };

    const showOnboardingRequired = () => {
        setModalTitle("Complete Onboarding");
        setModalMessage("Please complete platform onboarding to access this feature.");
        setShowModal(true);
    };

    const handleRedirect = (path: string) => {
        const routes: Record<string, string> = {
            document_setup: LinkFactory.document_setup_list,
            stream_type_setup: LinkFactory.stream_type_setup_list,
            applicant_type_setup: LinkFactory.applicant_type_setup_list,
            fee_setup: LinkFactory.fee_setup_list,
            stream_documents: LinkFactory.stream_documents_list,
            retainer_agreement: LinkFactory.admin_retainer_doc,
            initial_agreement: LinkFactory.admin_initial_doc,
        };
        const route = routes[path];
        if (route) {
            navigate(route);
        }
    };

    const isPinEnabled = async (): Promise<boolean> => {
        if (getFromLocal("PIN_ENABLED") === "true") return true;
        // The cached flag may be stale (e.g. PIN set from another device) — confirm with the server.
        try {
            const res = await axiosAgent.requests.post(api.RCIC_PROFILE + "/pin-set-status", {});
            const enabled = !!res?.data?.pinEnabled;
            saveToLocal("PIN_ENABLED", enabled ? "true" : "false");
            return enabled;
        } catch {
            return false;
        }
    };

    /** Asks for the PIN before opening a PIN-protected module, or has the RCIC set one on first access. */
    const openPinProtectedRoute = async (route: string) => {
        if (await isPinEnabled()) {
            setPendingPinRoute(route);
        } else {
            setPinSetupError('');
            setPinSetupRoute(route);
        }
    };

    const pinSetupValidation = Yup.object({
        newPin: Yup.string()
            .matches(/^\d{4}$/, "PIN must be exactly 4 numerical digits")
            .required("PIN is required"),
        confirmPin: Yup.string()
            .oneOf([Yup.ref("newPin")], "PINs must match")
            .required("Confirm PIN is required"),
    });

    const handleInitialPinSetup = (values: { newPin: string, confirmPin: string }) => {
        setLoader(true);
        setPinSetupError('');
        axiosAgent.requests.post(api.RCIC_PROFILE + "/update-pin", {pin: values.newPin, oldPin: ''}).then(res => {
            setLoader(false);
            if (res.data === true) {
                saveToLocal("PIN_ENABLED", "true");
                const route = pinSetupRoute;
                setPinSetupRoute(null);
                route && navigate(route);
            } else {
                setPinSetupError('Unable to set your PIN. Please try again.');
            }
        }).catch(() => {
            setLoader(false);
            setPinSetupError('Unable to set your PIN. Please try again.');
        });
    };

    function handleCaseAccess() {
        void (async () => {
            if (await ensureRcicCompletionStatus()) {
                await openPinProtectedRoute(LinkFactory.rcic_case_list);
            } else {
                setModalTitle("Complete Profile");
                setModalMessage("Please complete your profile setup to access this feature.");
                setShowModal(true);
            }
        })();
    }

    function handleWalletAccess() {
        void (async () => {
            if (await ensureRcicCompletionStatus()) {
                await openPinProtectedRoute(LinkFactory.rcic_wallet);
            } else {
                setModalTitle("Complete Profile");
                setModalMessage("Please complete your profile setup to access this feature.");
                setShowModal(true);
            }
        })();
    }

    const navigateToMessages = () => {
        navigate(LinkFactory.messages);
    };

    const isMyAnswersActive = location.pathname.startsWith(LinkFactory.my_answers);
    const isCommunityActive = location.pathname.startsWith(LinkFactory.community);
    const isMyQuestionsActive = location.pathname.startsWith(LinkFactory.my_questions);

    // Items that require legal agreements to be set up
    const isLocked = rcicCoreCompleted === true && legalSetupDone === false;

    // Pro subscription gating
    const { hasProAccess } = useProSubscription();

    const handleLockedClick = () => {
        setModalTitle("Legal Agreements Required");
        setModalMessage("Please complete your Legal Agreements setup (consultation agreement, service agreement, and signature) before accessing this feature.");
        setShowModal(true);
    };

    /** Returns className for a nav item that is locked behind legal setup */
    const lockedClass = (active: boolean) =>
        isLocked ? "side-nav-locked" : (active ? "side-nav-selected" : "");

    /** Returns the correct onClick for a locked nav item */
    const lockedClick = (handler: () => void) =>
        isLocked ? handleLockedClick : handler;

    return (
        <>
            <aside className="side-nav">

                <CustomModal
                    visible={showModal}
                    title={modalTitle}
                    message={modalMessage}
                    onClose={() => {
                        setShowModal(false);
                        if (modalTitle === "Reset Request") {
                            navigate(Utils.getDashboardPath());
                        }
                    }}
                ></CustomModal>

                <ul>
                    {UserTypes.CLIENT === userType ? (
                        <>
                            <NavGroup label="OVERVIEW" defaultExpanded>


                            <li
                                className={location.pathname.startsWith(LinkFactory.rcic_search) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.rcic_search)}
                            >
                                <span className="label">
                                    <FaSearch className="nav-icon" />
                                    Search RCIC
                                </span>
                            </li>

                            <li
                                className={location.pathname.startsWith(LinkFactory.subscriber_profile_page) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.subscriber_profile_page)}
                            >
                            <span className="label">
                                <FaTachometerAlt className="nav-icon" />
                                Dashboard
                            </span>
                            </li>
                            </NavGroup>

                            <NavGroup label="COMMUNICATION">
                            <li
                                className={location.pathname.startsWith(LinkFactory.notification_list) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.notification_list)}
                            >
                                <span className="label">
                                    <FaBell className="nav-icon" />
                                    Notifications
                                </span>
                            </li>

                            <li
                                className={location.pathname.startsWith(LinkFactory.messages) ? "side-nav-selected" : ""}
                                onClick={navigateToMessages}
                            >
                                <span className="label">
                                    <FaEnvelope className="nav-icon" />
                                    Messages
                                </span>
                            </li>
                            </NavGroup>

                            <NavGroup label="ENGAGEMENT">
                            <li
                                className={isMyQuestionsActive ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.my_questions)}
                            >
                                <span className="label">
                                    <FaCommentDots className="nav-icon" />
                                    Inquiries
                                </span>
                            </li>

                            <li
                                className={location.pathname.startsWith(LinkFactory.client_my_reviews) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.client_my_reviews)}
                            >
                                <span className="label">
                                    <FaStar className="nav-icon" />
                                    Reviews
                                </span>
                            </li>
                            </NavGroup>

                            <NavGroup label="FINANCE">
                            <li
                                className={location.pathname.startsWith(LinkFactory.client_payments) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.client_payments)}
                            >
                                <span className="label">
                                    <FaCreditCard className="nav-icon" />
                                    Payments
                                </span>
                            </li>
                            </NavGroup>

                        </>
                    ) : null}
                    {UserTypes.RCIC === userType && completedStep !== "1" ? (
                        <>
                            <NavGroup label="DASHBOARD" defaultExpanded>
                            <li
                                className={location.pathname.startsWith(LinkFactory.rcic_profile) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.rcic_profile)}
                            >
                                <span className="label">
                                    <FaUserCircle className="nav-icon" />
                                    Profile
                                </span>
                            </li>

                            <li
                                className={location.pathname.startsWith(LinkFactory.legal_correspondences) ? "side-nav-selected" : ""}
                                onClick={() => handleLegalAgreementsAccess()}
                            >
                                <span className="label">
                                    <FaFileContract className="nav-icon" />
                                    Legal Agreements
                                </span>
                            </li>

                            <li
                                className={location.pathname.startsWith(LinkFactory.appointment_slot_setup) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.appointment_slot_setup)}
                            >
                                <span className="label">
                                    <FaCalendarAlt className="nav-icon" />
                                    Schedule
                                </span>
                            </li>
                            </NavGroup>

                            <NavGroup label="OPERATIONS">

                                <li
                                    className={lockedClass(location.pathname.startsWith(LinkFactory.rcic_my_clients))}
                                    title={isLocked ? "Complete Legal Agreements setup to access" : undefined}
                                    onClick={lockedClick(() => handleRCICRedirection(LinkFactory.rcic_my_clients))}
                                >
                                <span className="label">
                                    <FaUsers className="nav-icon" />
                                    My Clients
                                </span>
                                </li>
                            <li
                                className={lockedClass(location.pathname.startsWith(LinkFactory.rcic_consultation_list))}
                                title={isLocked ? "Complete Legal Agreements setup to access" : undefined}
                                onClick={lockedClick(() => handleRCICRedirection(LinkFactory.rcic_consultation_list))}
                            >
                                <span className="label">
                                    <FaHandshake className="nav-icon" />
                                    Consultations
                                    {!isLocked && pendingConsultationCount > 0 && (
                                        <span className="new">New</span>
                                    )}
                                </span>
                            </li>



                            <li
                                className={lockedClass(location.pathname.startsWith(LinkFactory.rcic_case_list))}
                                title={isLocked ? "Complete Legal Agreements setup to access" : undefined}
                                onClick={lockedClick(() => handleCaseAccess())}
                            >
                                <span className="label">
                                    <FaBriefcase className="nav-icon" />
                                    Case Management
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" style={{marginLeft: '8px', display: 'inline-block'}}>
                                      <path d="M17 11V7C17 4.24 14.76 2 12 2C9.24 2 7 4.24 7 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <rect x="4" y="11" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <circle cx="12" cy="17" r="1.5" fill="currentColor"/>
                                    </svg>
                                </span>
                            </li>
                            </NavGroup>

                            <NavGroup label="FINANCE">
                            <li
                                className={lockedClass(location.pathname.startsWith(LinkFactory.rcic_wallet))}
                                title={isLocked ? "Complete Legal Agreements setup to access" : undefined}
                                onClick={lockedClick(() => handleWalletAccess())}
                            >
                                <span className="label">
                                    <FaWallet className="nav-icon" />
                                    Wallet
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" style={{marginLeft: '8px', display: 'inline-block'}}>
                                      <path d="M17 11V7C17 4.24 14.76 2 12 2C9.24 2 7 4.24 7 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <rect x="4" y="11" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <circle cx="12" cy="17" r="1.5" fill="currentColor"/>
                                    </svg>
                                </span>
                            </li>
                            </NavGroup>

                            <NavGroup label="COMMUNICATION">
                            <li
                                className={lockedClass(location.pathname.startsWith(LinkFactory.notification_list))}
                                title={isLocked ? "Complete Legal Agreements setup to access" : undefined}
                                onClick={lockedClick(() => handleRCICRedirection(LinkFactory.notification_list))}
                            >
                                <span className="label">
                                    <FaBell className="nav-icon" />
                                    Notifications
                                </span>
                            </li>

                            <li
                                className={lockedClass(location.pathname.startsWith(LinkFactory.messages))}
                                title={isLocked ? "Complete Legal Agreements setup to access" : undefined}
                                onClick={lockedClick(() => handleRCICRedirection(LinkFactory.messages))}
                            >
                                <span className="label">
                                    <FaEnvelope className="nav-icon" />
                                    Messages
                                </span>
                            </li>

                            </NavGroup>

                            <NavGroup label="ENGAGEMENT">
                            <li
                                className={lockedClass(isMyAnswersActive)}
                                title={isLocked ? "Complete Legal Agreements setup to access" : undefined}
                                onClick={lockedClick(() => handleRCICRedirection(LinkFactory.my_answers))}
                            >
                                <span className="label">
                                    <FaCommentDots className="nav-icon" />
                                    Expert Answers
                                </span>
                            </li>

                            <li
                                className={lockedClass(isCommunityActive)}
                                title={isLocked ? "Complete Legal Agreements setup to access" : undefined}
                                onClick={lockedClick(() => handleRCICRedirection(LinkFactory.community))}
                            >
                                <span className="label">
                                    <FaGlobeAmericas className="nav-icon" />
                                    RCIC Community
                                </span>
                            </li>

                            <li
                                className={lockedClass(location.pathname.startsWith(LinkFactory.rcic_my_reviews))}
                                title={isLocked ? "Complete Legal Agreements setup to access" : undefined}
                                onClick={lockedClick(() => handleRCICRedirection(LinkFactory.rcic_my_reviews))}
                            >
                                <span className="label">
                                    <FaStar className="nav-icon" />
                                    Client Reviews
                                </span>
                            </li>
                            </NavGroup>

                            {hasProAccess && <li className="side-nav-divider" aria-hidden="true"/>}
                            {hasProAccess ? (
                                <NavGroup
                                    label="PRO WORKSPACE"
                                    className="side-nav-workspace-label"
                                    style={{marginTop: "0.5rem"}}
                                    icon={<FaCrown className="side-nav-group-icon" />}
                                >
                                    <li
                                        className={location.pathname.startsWith(LinkFactory.rcic_action_centre) ? "side-nav-selected" : ""}
                                        onClick={() => navigate(LinkFactory.rcic_action_centre)}
                                    >
                                        <span className="label">
                                            <FaBars className="nav-icon" />
                                            Action Center
                                        </span>
                                    </li>
                                    <li
                                        className={location.pathname.startsWith(LinkFactory.rcic_ai_practice_assistant) ? "side-nav-selected" : ""}
                                        onClick={() => navigate(LinkFactory.rcic_ai_practice_assistant)}
                                    >
                                        <span className="label">
                                            <FaLightbulb className="nav-icon" />
                                            Practice Intelligence
                                        </span>
                                    </li>
                                    <li
                                        className={lockedClass(location.pathname.startsWith(LinkFactory.practice_continuity))}
                                        title={isLocked ? "Complete Legal Agreements setup to access" : undefined}
                                        onClick={lockedClick(() => navigate(LinkFactory.practice_continuity))}
                                    >
                                        <span className="label">
                                            <FaPeopleArrows className="nav-icon" />
                                            Practice Continuity
                                            {!isLocked && pendingPracticeContinuityCount > 0 && (
                                                <span className="new">New</span>
                                            )}
                                        </span>
                                    </li>
                                    <li
                                        className={location.pathname.startsWith(LinkFactory.rcic_global_pricing) ? "side-nav-selected" : ""}
                                        onClick={() => navigate(LinkFactory.rcic_global_pricing)}
                                    >
                                        <span className="label">
                                            <FaGlobeAmericas className="nav-icon" />
                                            Regional Pricing
                                        </span>
                                    </li>
                                </NavGroup>
                            ) : null}

                        </>
                    ) : null}
                    {UserTypes.ADMIN === userType ? (
                        <>
                            <NavGroup label="OVERVIEW" style={{marginTop: "1.5rem"}}>
                            <li
                                className={location.pathname.startsWith(LinkFactory.admin_dashboard) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.admin_dashboard)}
                            >
                                <span className="label">
                                    <FaChartPie className="nav-icon" />
                                    Dashboard
                                </span>
                            </li>

                            </NavGroup>

                            <NavGroup label="USER MANAGEMENT">
                            <li
                                className={location.pathname.startsWith(LinkFactory.admin_rcic_officers) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.admin_rcic_officers)}
                            >
                                <span className="label">
                                    <FaUserTie className="nav-icon" />
                                    Consultants
                                </span>
                            </li>
                            <li
                                className={location.pathname.startsWith(LinkFactory.admin_clients) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.admin_clients)}
                            >
                                <span className="label">
                                    <FaUsers className="nav-icon" />
                                    Clients
                                </span>
                            </li>

                            </NavGroup>

                            <NavGroup label="REQUESTS">
                            <li
                                className={location.pathname.startsWith(LinkFactory.admin_refund_queue) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.admin_refund_queue)}
                            >
                                <span className="label">
                                    <FaUndo className="nav-icon" />
                                    Refund Request
                                </span>
                            </li>
                            <li
                                className={location.pathname.startsWith(LinkFactory.admin_client_account_payment) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.admin_client_account_payment)}
                            >
                                <span className="label">
                                    <FaCreditCard className="nav-icon" />
                                    Client Account Request
                                </span>
                            </li>
                                <li
                                    className={location.pathname.startsWith(LinkFactory.withdrawal_request_list) ? "side-nav-selected" : ""}
                                    onClick={() => navigate(LinkFactory.withdrawal_request_list)}
                                >
                                <span className="label">
                                    <FaMoneyCheckAlt className="nav-icon" />
                                    Withdrawal Requests
                                </span>
                                </li>

                            </NavGroup>

                            <NavGroup label="TRANSACTIONS">
                            <li
                                className={location.pathname.startsWith(LinkFactory.admin_service_purchases) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.admin_service_purchases)}
                            >
                                <span className="label">
                                    <FaExchangeAlt className="nav-icon" />
                                    Gateway Payments
                                </span>
                            </li>
                            <li
                                className={location.pathname.startsWith(LinkFactory.admin_platform_revenue) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.admin_platform_revenue)}
                            >
                                <span className="label">
                                    <FaCrown className="nav-icon" />
                                    Platform Revenue
                                </span>
                            </li>
                            <li
                                className={location.pathname.startsWith(LinkFactory.admin_commission_invoices) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.admin_commission_invoices)}
                            >
                                <span className="label">
                                    <FaFileInvoiceDollar className="nav-icon" />
                                    Commission Invoices
                                </span>
                            </li>

                            <li
                                className={location.pathname.startsWith(LinkFactory.admin_rcic_wallet_withdrawals) ? "side-nav-selected" : ""}
                                onClick={() => navigate(LinkFactory.admin_rcic_wallet_withdrawals)}
                            >
                                <span className="label">
                                    <FaWallet className="nav-icon" />
                                    Local Withdrawals
                                </span>
                            </li>

                            </NavGroup>

                            <NavGroup label="SYSTEM SETUP">
                            <li
                                className={location.pathname.startsWith(LinkFactory.applicant_type_setup_list) ? "side-nav-selected" : ""}
                                onClick={() => handleRedirect('applicant_type_setup')}
                            >
                                <span className="label">
                                    <FaIdCard className="nav-icon" />
                                    Applicant Type Setup
                                </span>
                            </li>
                            <li
                                className={location.pathname.startsWith(LinkFactory.stream_type_setup_list) ? "side-nav-selected" : ""}
                                onClick={() => handleRedirect('stream_type_setup')}
                            >
                                <span className="label">
                                    <FaStream className="nav-icon" />
                                    Stream Type Setup
                                </span>
                            </li>
                            <li
                                className={location.pathname.startsWith(LinkFactory.fee_setup_list) ? "side-nav-selected" : ""}
                                onClick={() => handleRedirect('fee_setup')}
                            >
                                <span className="label">
                                    <FaMoneyBillWave className="nav-icon" />
                                    Fee Setup
                                </span>
                            </li>
                            <li
                                className={location.pathname.startsWith(LinkFactory.document_setup_list) ? "side-nav-selected" : ""}
                                onClick={() => handleRedirect('document_setup')}
                            >
                                <span className="label">
                                    <FaFileMedical className="nav-icon" />
                                    Document Setup
                                </span>
                            </li>
                            <li
                                className={location.pathname.startsWith(LinkFactory.stream_documents_list) ? "side-nav-selected" : ""}
                                onClick={() => handleRedirect('stream_documents')}
                            >
                                <span className="label">
                                    <FaRoute className="nav-icon" />
                                    Document Mapping
                                </span>
                            </li>

                            </NavGroup>

                            <NavGroup label="TEMPLATES">
                            <li
                                className={location.pathname.startsWith(LinkFactory.admin_retainer_doc) ? "side-nav-selected" : ""}
                                onClick={() => handleRedirect('retainer_agreement')}
                            >
                                <span className="label">
                                    <FaFileContract className="nav-icon" />
                                    Service Agreement
                                </span>
                            </li>
                            <li
                                className={location.pathname.startsWith(LinkFactory.admin_initial_doc) ? "side-nav-selected" : ""}
                                onClick={() => handleRedirect('initial_agreement')}
                            >
                                <span className="label">
                                    <FaFileSignature className="nav-icon" />
                                    Initial Agreement
                                </span>
                            </li>
                            </NavGroup>

                        </>
                    ) : null}
                </ul>

            </aside>
            {pendingPinRoute !== null && (
                <div className="popup-overlay">
                    <div className="popup-content popup-small">
                        <div className="popup-body">
                            <Loader visible={loader}/>
                            <div className="case-pin-icon">
                                <img
                                    src={privacyIcon}
                                    alt="privacy icon"
                                    style={{width: "70px", height: "70px"}}
                                />
                            </div>

                            <div className="case-pin-greetings">
                                <h3 className="pin-title">Enter PIN</h3>
                                <p className="pin-subtitle">Please enter your secure <strong>PIN</strong> to access this module.</p>
                            </div>

                            <div className="form-group mb-0">
                                <div className="case-pin-field-wrapper">
                                    {Array.from({length: 4}).map((_, index) => (
                                        <input
                                            key={`pinInput-${index}`}
                                            type={showPin ? "text" : "password"}
                                            id={`pinInput-${index}`}
                                            className="form-control pin-input"
                                            maxLength={1}
                                            autoFocus={index === 0}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d$/.test(value)) {
                                                    const nextInput = document.getElementById(`pinInput-${index + 1}`);
                                                    if (nextInput) {
                                                        nextInput.focus();
                                                    }
                                                } else {
                                                    e.target.value = "";
                                                }
                                                const pin = Array.from({length: 4})
                                                    .map((_, i) => (document.getElementById(`pinInput-${i}`) as HTMLInputElement)?.value || "")
                                                    .join("");
                                                if (pin.length === 4) {
                                                    pinSet(pin);
                                                    setDisableAccessButton(false);
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                e.key === "Backspace" && setDisableAccessButton(true);
                                                if (e.key === "Backspace" && !e.currentTarget.value) {
                                                    const prevInput = document.getElementById(`pinInput-${index - 1}`);
                                                    if (prevInput) {
                                                        prevInput.focus();
                                                    }
                                                }
                                                if (index === 3 && e.currentTarget.value && e.key === "Enter") {
                                                    e.preventDefault();
                                                    verifyPin();
                                                }
                                            }}
                                        />
                                    ))}
                                </div>

                                {invalidPin.length > 0 ? (
                                    <div className="text-danger text-small text-center mt-3">{invalidPin}</div>
                                ) : ''}
                            </div>

                            <div className="forgot-pin-section">
                                <span className="forgot-pin-text">Forgot PIN?</span>
                                <button
                                    type="button"
                                    className="reset-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        forgetPinReq();
                                    }}
                                >
                                    Reset Here
                                </button>
                            </div>

                            <div className="d-flex justify-content-center gap-2 mt-2" style={{paddingLeft: "1rem", paddingRight: "1rem"}}>
                                <button
                                    className="btn btn-action w-100"
                                    onClick={() => {
                                        setPendingPinRoute(null);
                                        resetPinInput();
                                        setInvalidPin('');
                                        setDisableAccessButton(true);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-brand w-100"
                                    onClick={() => {
                                        verifyPin();
                                    }}
                                    disabled={disableAccessButton || loader}
                                >
                                    {loader ? 'Verifying...' : 'Access'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {pinSetupRoute !== null && (
                <div className="popup-overlay">
                    <div className="popup-content popup-small">
                        <div className="popup-body">
                            <Loader visible={loader}/>
                            <div className="case-pin-icon">
                                <img
                                    src={privacyIcon}
                                    alt="privacy icon"
                                    style={{width: "70px", height: "70px"}}
                                />
                            </div>
                            <div className="case-pin-greetings">
                                <h3 className="pin-title">Set Your Security PIN</h3>
                                <p className="pin-subtitle">
                                    Case Management and Wallet are protected by a 4-digit PIN. Set your PIN to continue — you will use it each time you access these modules.
                                </p>
                            </div>
                            <Formik
                                initialValues={{newPin: '', confirmPin: ''}}
                                onSubmit={handleInitialPinSetup}
                                validationSchema={pinSetupValidation}
                                validateOnChange={false}
                                validateOnBlur={true}
                            >
                                {({setFieldValue}) => (
                                    <Form style={{margin: '0 1.5rem'}}>
                                        <div className="form-group mb-3">
                                            <Field
                                                name="newPin"
                                                type="password"
                                                placeholder="Enter PIN"
                                                className="form-control"
                                                autoComplete="new-password"
                                                inputMode="numeric"
                                                autoFocus
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    setFieldValue("newPin", e.target.value.replace(/\D/g, '').slice(0, 4))}
                                            />
                                            <ErrorMessage name="newPin">
                                                {msg => <div className="field-error">{msg}</div>}
                                            </ErrorMessage>
                                        </div>
                                        <div className="form-group mb-3">
                                            <Field
                                                name="confirmPin"
                                                type="password"
                                                placeholder="Re-enter PIN"
                                                className="form-control"
                                                autoComplete="new-password"
                                                inputMode="numeric"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    setFieldValue("confirmPin", e.target.value.replace(/\D/g, '').slice(0, 4))}
                                            />
                                            <ErrorMessage name="confirmPin">
                                                {msg => <div className="field-error">{msg}</div>}
                                            </ErrorMessage>
                                        </div>

                                        {pinSetupError.length > 0 ? (
                                            <div className="text-danger text-small text-center mb-2">{pinSetupError}</div>
                                        ) : ''}

                                        <div className="d-flex justify-content-center gap-2 mt-2">
                                            <button
                                                type="button"
                                                className="btn btn-action w-100"
                                                onClick={() => setPinSetupRoute(null)}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-brand w-100"
                                                disabled={loader}
                                            >
                                                {loader ? 'Saving...' : 'Set PIN & Continue'}
                                            </button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SideNav;
